import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent } from "@shopflo/ui/sheet";
import { motion } from "motion/react";
import { AddressCardFields } from "lib/types/address";
import { concateAddresses } from "lib/utils/address";
import { toTitleCase } from "lib/utils/helpers";
import { FadedMap, MapIcon, PinIcon } from "assests/icons/CartIcons";
import { useLocale } from "lib/hooks/useLocale";
import { Button } from "@shopflo/ui/button";

const PIN_EASE: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

interface DelhiveryConfirmationPopUpProps {
  isOpen: boolean;
  closePopup: () => void;
  onChangeClick: () => void;
  address?: AddressCardFields;
  durationSeconds?: number;
}

const DelhiveryConfirmationPopUp: React.FC<DelhiveryConfirmationPopUpProps> = ({
  isOpen,
  closePopup,
  onChangeClick,
  address,
  durationSeconds = 3000,
}) => {
  const { t } = useLocale();

  const durationInSeconds = durationSeconds / 1000;
  const [pinLanded, setPinLanded] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPinLanded(false);
      return;
    }
    const fallback = setTimeout(() => setPinLanded(true), 1500);// fallback to true if pin doesn't land after 1.5 seconds
    return () => clearTimeout(fallback);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !pinLanded) return;
    const timer = setTimeout(closePopup, durationSeconds);
    return () => clearTimeout(timer);
  }, [isOpen, closePopup, pinLanded]);

  const addressLabel = useMemo(() => {
    if (!address?.type) return "";
    return toTitleCase(address.type);
  }, [address?.type]);

  const addressLine = useMemo(() => {
    const line = concateAddresses(
      address?.address1 ?? "",
      address?.address2 ?? "",
      address?.city ?? "",
      address?.state ?? "",
      address?.country ?? "",
      address?.zip ?? "",
    );
    return address?.name ? `${address.name} · ${line}` : line;
  }, [address]);
  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closePopup()}>
      <SheetContent
        side="bottom"
        withinContainer
        container={document.getElementById("checkout-ui-root")}
        style={{
          background: "linear-gradient(180deg, rgba(255, 255, 255, 0.10) 2.38%, #FFF 73.31%)",
          backdropFilter: "blur(1.5px)",
        }}
        className="!h-full !overflow-y-auto !border-0 !mt-auto"
        overlayClassName="!bg-transparent">
        <div className="flex flex-1 flex-col items-center justify-end w-full h-full pb-8">
          <div
            className="relative mb-4 inline-flex items-center justify-center overflow-hidden">
            <div className="relative">
              <motion.div
                initial={{ opacity: 1 }}
                animate={{ opacity: pinLanded ? 0 : 1 }}
                transition={{ duration: durationInSeconds, ease: "linear" }}>
                <MapIcon />
              </motion.div>

              <motion.div
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: pinLanded ? 1 : 0 }}
                transition={{ duration: durationInSeconds, ease: "linear" }}>
                <FadedMap />
              </motion.div>
            </div>

            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, y: 32, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.45, ease: PIN_EASE, delay: 0.2 }}
              onAnimationComplete={() => setPinLanded(true)}>
              <PinIcon />
            </motion.div>
          </div>

          <div className="flex flex-col items-center gap-2 w-[18.5rem] mb-8 break-words">
            <p className="text-text-md-bold text-zinc-800">Delivering to</p>
            {Boolean(addressLabel) && (
              <div className="flex items-center justify-center rounded-lg bg-black/[0.04] px-1.5">
                <p className="text-text-sm-semibold text-coal-light">{addressLabel}</p>
              </div>
            )}
            <p className="text-text-sm-medium text-zinc-800 text-center opacity-70 [overflow-wrap:anywhere]">{addressLine}</p>
          </div>

          <div className="w-full px-6">
            <div className="relative overflow-hidden rounded-xl">
              <Button
                variant="ghost"
                size="xl"
                className="w-full !rounded-lg "
                style={{ background: "var(--flo-primary-dark-color)", color: "var(--flo-primary-btn-text-color)" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeClick();
                }}
              >
                {t("delhivery_confirmation_popup_btn_text")}
              </Button>
              {pinLanded && (
                <div
                  className="absolute top-0 right-0 h-full w-full bg-white opacity-20 animate-shrink-width pointer-events-none"
                  style={{ animationDuration: `${durationInSeconds}s` }}
                />
              )}
            </div>
          </div>

          <div className="w-full px-6 mt-2">
            <Button
              variant="ghost"
              size="xl"
              className="w-full border border-black/5 !rounded-lg hover:!bg-transparent"
              style={{ color: "var(--flo-primary-dark-color)" }}
              onClick={(e) => {
                e.stopPropagation();
                closePopup();
              }}
            >
              {t("skip")}
            </Button>
          </div>
        </div>  
      </SheetContent>
    </Sheet>
  );
};

export default DelhiveryConfirmationPopUp;
